"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FiMaximize2 } from "react-icons/fi";
import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import ExpiryBadge from "@/components/patient/documents/expiry-badge";
import IdDocumentPreview from "@/components/patient/documents/id-document-preview";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { showsPatientIdentificationCard } from "@/lib/tenant-flags";
import type { PatientDetail } from "@/types/patient";
import { getSignedUrl } from "@/api/auth/gcs";
import { documentQueryOptions } from "@/api/provider/user/patient/document-query";

// GCS signed URLs live ~15m; treat ours as fresh for 10m so React Query
// re-signs before expiry while the chart stays open.
const ID_SIGNED_URL_STALE_MS = 10 * 60 * 1000;
interface IdentificationSectionProps {
  patient: PatientDetail;
}

/**
 * Pins the patient's most recent identity document (driver's license, photo ID,
 * passport, etc.) near the top of the chart. Enabled per-tenant (see
 * showsPatientIdentificationCard); renders nothing when the tenant isn't
 * enabled, the user lacks document-read access, or the patient has no ID on
 * file.
 */
export default function IdentificationSection(t0) {
  const $ = _c(52);
  const {
    patient
  } = t0;
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOCUMENTS_READ);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canViewDocuments = t1;
  let t2;
  if ($[2] !== tenant) {
    t2 = showsPatientIdentificationCard(tenant);
    $[2] = tenant;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tenantEnabled = t2;
  let t3;
  if ($[4] !== patient.id) {
    t3 = documentQueryOptions.idDocuments(patient.id);
    $[4] = patient.id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = canViewDocuments && tenantEnabled;
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: t6
  } = useQuery(t5);
  let t7;
  if ($[9] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const idDocuments = t7;
  let t8;
  bb0: {
    if (idDocuments.length === 0) {
      t8 = null;
      break bb0;
    }
    let t9;
    if ($[11] !== idDocuments) {
      t9 = [...idDocuments].sort(_temp);
      $[11] = idDocuments;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    t8 = t9[0];
  }
  const mostRecentId = t8;
  const [viewOpen, setViewOpen] = useState(false);
  const t9 = mostRecentId?.filePath;
  let t10;
  if ($[13] !== t9) {
    t10 = ["gcs", "id-document", t9];
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== mostRecentId) {
    t11 = () => getSignedUrl(mostRecentId.filePath);
    $[15] = mostRecentId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = !!mostRecentId;
  let t13;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12) {
    t13 = {
      queryKey: t10,
      queryFn: t11,
      enabled: t12,
      staleTime: ID_SIGNED_URL_STALE_MS
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const {
    data: signedPreview,
    isLoading: isSigningPreview
  } = useQuery(t13);
  if (!canViewDocuments || !tenantEnabled || !mostRecentId) {
    return null;
  }
  const previewUrl = signedPreview?.signedUrl;
  let t14;
  if ($[21] !== mostRecentId.createdDate) {
    t14 = mostRecentId.createdDate && <span className="shrink-0 text-caption text-foreground-secondary">Added {formatDate(mostRecentId.createdDate)}</span>;
    $[21] = mostRecentId.createdDate;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== isSigningPreview || $[24] !== mostRecentId || $[25] !== previewUrl) {
    t15 = <IdDocumentPreview document={mostRecentId} signedUrl={previewUrl} isLoading={isSigningPreview} heightClassName="h-72" />;
    $[23] = isSigningPreview;
    $[24] = mostRecentId;
    $[25] = previewUrl;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setViewOpen(true);
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = `View identity document: ${mostRecentId.title}`;
  let t18;
  if ($[28] !== t17) {
    t18 = <button type="button" onClick={t16} aria-label={t17} className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring" />;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="pointer-events-none absolute right-2 top-2 hidden rounded-md bg-background/80 p-1.5 text-foreground-secondary backdrop-blur-sm group-hover:flex"><FiMaximize2 className="h-4 w-4" /></span>;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t15 || $[32] !== t18) {
    t20 = <div className="group relative w-full max-w-md overflow-hidden rounded-lg border border-border bg-background-secondary">{t15}{t18}{t19}</div>;
    $[31] = t15;
    $[32] = t18;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== mostRecentId.title) {
    t21 = <p className="truncate text-caption text-foreground-secondary">{mostRecentId.title}</p>;
    $[34] = mostRecentId.title;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== mostRecentId.expiryDate) {
    t22 = mostRecentId.expiryDate && <span className="text-caption text-foreground-secondary">· Expires {formatDate(mostRecentId.expiryDate)}</span>;
    $[36] = mostRecentId.expiryDate;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== mostRecentId.expiryDate) {
    t23 = <ExpiryBadge expiryDate={mostRecentId.expiryDate} onlyWhenNoteworthy={true} />;
    $[38] = mostRecentId.expiryDate;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t21 || $[41] !== t22 || $[42] !== t23) {
    t24 = <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">{t21}{t22}{t23}</div>;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== mostRecentId || $[45] !== viewOpen) {
    t25 = <DocumentViewDialog isOpen={viewOpen} onOpenChange={setViewOpen} document={mostRecentId} />;
    $[44] = mostRecentId;
    $[45] = viewOpen;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t14 || $[48] !== t20 || $[49] !== t24 || $[50] !== t25) {
    t26 = <CollapsibleSection title="Identification" persistKey="identification" id="identification-section" actions={t14}>{t20}{t24}{t25}</CollapsibleSection>;
    $[47] = t14;
    $[48] = t20;
    $[49] = t24;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  return t26;
}
function _temp(a, b) {
  const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
  const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
  return bTime - aTime;
}